/**
 * Wire schemas for the Operations Under Review book surface:
 * leads that have been routed and are still circulating,
 * not yet committed to a case (Accepted+) and not yet terminal.
 *
 * The Under Review book is a liminal space, not a home. A lead in it should be
 * heading toward one of two exits — *becomes a case* (Accepted → Placed)
 * or *moves out* (Rejected / Withdrawn). The problem the surface solves
 * is **bloat**: leads that have stopped flowing toward either exit and
 * are accumulating. The organizing axis is therefore TIME.
 *
 * Two clocks, both derived (no stored state — the Lead's status plus its
 * placements are the truth):
 *   - `totalAgeDays`           — how long the lead has been circulating
 *                                (since its first placement was Sent).
 *                                The primary sort. No later firm
 *                                response or decision resets this — it is
 *                                the honest clog signal.
 *   - `currentPositionAgeDays` — time since the current (most-recent)
 *                                placement last moved. Secondary; a firm
 *                                response that decides nothing resets it,
 *                                which is exactly why total age leads.
 *
 * Surface = time (the bars + clocks + flags). Substrate = quality (the
 * decline reasons + outreach logs + case summary, reached by digging in).
 * v1 *shows* the quality evidence; it does not *score* it — firm-behaviour
 * scoring is Firm Performance (Client Success, deferred per ADR-0036).
 *
 * Spec: engineering/command-center/docs/port-notes/whirlpool.md (evicted 2026-08-29, #775 round 10)
 */

import { z } from "zod";
import { LeadSchema } from "./lead.js";

/**
 * One firm's leg of a lead's journey through the Under Review book — a single
 * Placement, projected for the trail the operator digs into. The decline
 * reason and outreach log are the *evidence* the operator reads to judge
 * a clog's cause (did the firm dump it, or work it?); the surface
 * displays them, it does not evaluate them.
 */
export const UnderReviewPlacementSchema = z.object({
  /** Handling Firm name, or null when the firm lookup missed. */
  firmName: z.string().nullable(),
  /** Placement Decision (Sent, Reviewing, Declined, Pulled, …), or null. */
  decision: z.string().nullable(),
  /** ISO-8601 datetime the placement was first sent, or null (Assigned). */
  dateSent: z.string().nullable(),
  /** ISO-8601 datetime the firm last responded, or null. */
  dateResponded: z.string().nullable(),
  /** The firm's decline rationale (multilineText), or null. Evidence. */
  declineReason: z.string().nullable(),
  /** The firm's outreach log (multilineText), or null. Evidence. */
  outreachLog: z.string().nullable(),
});
export type UnderReviewPlacement = z.infer<typeof UnderReviewPlacementSchema>;

/**
 * One stretch of the lead's custody timeline — who held the lead, and for
 * how long. The bar is built from these: each firm's stretch, plus a
 * **LegalFlare** stretch for any time the lead sat declined/pulled and not
 * yet rerouted (that time is LF's, not a firm's — "we own it"). Segments
 * telescope across the whole age (first Sent → now), so they sum to the
 * total. The bar colours each segment by ITS OWN days (≤5 green / 6–10
 * yellow / 11+ red), so a fat stuck stretch turns red on sight.
 */
export const UnderReviewSegmentSchema = z.object({
  /** Who held the lead during this stretch. `legalflare` = no firm had it
   *  (declined/pulled, awaiting reroute) — the time is on LF. */
  holder: z.enum(["firm", "legalflare"]),
  /** Firm name when `holder === "firm"`; null for LegalFlare stretches. */
  firmName: z.string().nullable(),
  /** Days the lead spent in this holder's hands. Clamped ≥ 0. */
  days: z.number(),
});
export type UnderReviewSegment = z.infer<typeof UnderReviewSegmentSchema>;

/**
 * One lead in the Under Review book — the typed Lead plus the resolved OF name,
 * the two derived clocks, the current standoff, the glance-level flags,
 * and the full firm trail. All joins/derivations are done by the
 * composer so the UI renders without its own lookups.
 */
export const UnderReviewLeadSchema = LeadSchema.extend({
  /** Joined `Firm Name` for `originatingFirmId`, or null. Orientation. */
  originatingFirmName: z.string().nullable(),

  /** SURFACE — total days circulating (since first placement Sent).
   *  The worst-first sort key. Clamped to ≥ 0. */
  totalAgeDays: z.number(),
  /** SURFACE — days since the current placement last moved. Clamped ≥ 0. */
  currentPositionAgeDays: z.number(),

  /** SURFACE — days since the CURRENT holder last logged outreach (the
   *  newest `[<ISO>]` entry in its Outreach Log). The "is this actually
   *  being worked?" read: an old lead with recent outreach is *flowing*,
   *  not clogged — it lets the operator clear a false positive (old by the
   *  clock, but a firm is actively working it) at a glance. Null when no
   *  live firm holds it (`declinedStuck` — the clog is LF's idle reroute,
   *  not a firm's silence) or the current firm has logged nothing yet.
   *  SHOWN, never sorted on: outreach is firm self-report, and sorting by
   *  it would let a firm sink a real stall with one token line — the exact
   *  gaming `totalAgeDays` exists to resist. Clamped ≥ 0. */
  lastOutreachDays: z.number().nullable(),

  /** The firm currently holding it (most-recent placement), or null. */
  currentFirmName: z.string().nullable(),
  /** The current placement's decision, or null. */
  currentDecision: z.string().nullable(),
  /** The current (most-recent) placement's record id — the target an
   *  operator action (accept/decline) writes to. Always present: an
   *  Under Review lead has ≥ 1 placement by the drain line. */
  currentPlacementId: z.string(),

  /** The current (most-recent) placement is Declined/Pulled and nothing
   *  has re-placed it — the system is idle, waiting on the operator to
   *  reroute. The sharpest clog: nobody is acting but you. */
  declinedStuck: z.boolean(),
  /** Any placement on this lead carries a decline reason — a glance-level
   *  "evidence available" marker on the collapsed row. */
  hasDeclineReasons: z.boolean(),

  /** The custody timeline for the bar — firm stretches + LegalFlare
   *  stretches, oldest first, summing to the total age. */
  segments: z.array(UnderReviewSegmentSchema),

  /** The journey: every placement this lead has had, oldest first. */
  trail: z.array(UnderReviewPlacementSchema),
});
export type UnderReviewLead = z.infer<typeof UnderReviewLeadSchema>;

/**
 * The catch-all **Miscellaneous** statuses — non-terminal Lead Statuses that
 * no dedicated surface owns. These are the pre-/around-routing limbo states:
 * a lead awaiting routing (**Unreviewed**), one routing found no firm for
 * (**Unmatched**), one missing required fields (**Incomplete**), a Transfer
 * awaiting its first route (**Pending**), or one bounced back for another
 * placement (**Reroute** / **Re-present**). They typically have no custody
 * trail yet — the whole point is they haven't moved — so they ride a leaner
 * row than the circulating leads (see `UnderReviewMiscSchema`).
 *
 * NOT here: **Under Review** (the circulating Unsigned/Transfers tabs),
 * **Placed** (Placed book), **Settled** (Settled book), and **Dropped /
 * Rejected / Case Closed** (their own surfaces, deferred). The Miscellaneous
 * tab exists so a lead in one of these limbo states is never invisible.
 */
export const UNDER_REVIEW_MISC_STATUSES = [
  "Unreviewed",
  "Unmatched",
  "Incomplete",
  "Pending",
  "Reroute",
  "Re-present",
] as const;

/**
 * One lead in the **Miscellaneous** tab — a limbo-state matter with no
 * routing trail to show. Leaner than `UnderReviewLead`: just who it is, why
 * it's stuck (its Lead Status), and how long it's waited (since creation).
 */
export const UnderReviewMiscSchema = z.object({
  /** Lead record ID. */
  id: z.string(),
  /** Stable human-readable Lead ID (Airtable primary text). */
  leadId: z.string(),
  /** Client name. */
  name: z.string(),
  /** The limbo Lead Status — the reason it's here (Unmatched / Incomplete /
   *  …). Shown as the row's badge so the operator reads the blocker. */
  status: z.string().nullable(),
  /** TRUE for a Transfer (already-retained client) — higher stakes; marked. */
  signed: z.boolean(),
  /** Case-type label, or null. */
  caseType: z.string().nullable(),
  /** Narrative case summary — backs the reused case-summary side panel. */
  caseSummary: z.string().nullable(),
  /** Two-letter incident state — orientation chip. */
  incidentState: z.string().nullable(),
  /** Joined Originating Firm name, or null. */
  originatingFirmName: z.string().nullable(),
  /** SURFACE — days since the matter was created (its real `Creation Date`,
   *  fallback the system `createdTime`). How long it's sat in limbo; the
   *  worst-first (longest-waiting) sort key. Clamped ≥ 0. */
  ageDays: z.number(),
  /** The matter's Creation Date (YYYY-MM-DD) — shown as "Received {date}" in
   *  the panel header. Null when Airtable omits it. */
  receivedDate: z.string().nullable(),
});
export type UnderReviewMisc = z.infer<typeof UnderReviewMiscSchema>;

/**
 * Payload for `GET /api/under-review`.
 *   - `leads` — the circulating population (Under Review), sorted
 *     worst-clog-first; the UI splits it into the Unsigned/Transfers tabs on
 *     `signed`.
 *   - `miscellaneous` — the limbo-state catch-all (see UNDER_REVIEW_MISC_STATUSES),
 *     sorted longest-waiting first; backs the Miscellaneous tab.
 */
export const UnderReviewResponseSchema = z.object({
  leads: z.array(UnderReviewLeadSchema),
  miscellaneous: z.array(UnderReviewMiscSchema),
});
export type UnderReviewResponse = z.infer<typeof UnderReviewResponseSchema>;
